/**
 * Panel de **historial de cotizaciones** (réplica de la pestaña Historial del SSR, solo admin).
 *
 * Consume `GET /api/v1/cotizador/cotizaciones` vía `useCotizaciones` (TanStack Query),
 * filtrado por la sede activa (el backend lo resuelve por `X-Sede-Id`). El listado es solo
 * admin (la API devuelve 403 si no): este panel solo lo monta `CotizadorScreen` para admins.
 * Estados de carga/vacío/error siempre contemplados (no pantalla en blanco).
 */
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import {
  cotizadorErrorMessage,
  formatHistoryDate,
  formatHistoryMedidas,
  formatMoney,
  modeloLabel,
} from "../format";
import { useCotizaciones } from "../hooks/useCotizador";

export function HistoryPanel() {
  // `mine=false`: el admin ve todas las cotizaciones de su sede (igual que el SSR, que pedía
  // `mine=0` para el admin). La query se dispara al montar el panel (la pestaña ya está activa).
  const { data, isLoading, isError, error } = useCotizaciones(true, 50, false);
  const items = data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Historial de cotizaciones</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-slate-500" role="status">
            Cargando…
          </p>
        ) : isError ? (
          <p className="text-sm text-red-600" role="alert">
            {cotizadorErrorMessage(error)}
          </p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">Aún no hay registros.</p>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Fecha</TableHeaderCell>
                <TableHeaderCell>Persona</TableHeaderCell>
                <TableHeaderCell>Modelo</TableHeaderCell>
                <TableHeaderCell>Medidas</TableHeaderCell>
                <TableHeaderCell className="text-right">Total</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{formatHistoryDate(item.created_at)}</TableCell>
                  <TableCell>{item.user_name ?? item.user_id ?? "—"}</TableCell>
                  <TableCell>
                    {modeloLabel(item.modelo_id)} / Cal {item.calibre}
                  </TableCell>
                  <TableCell>{formatHistoryMedidas(item)}</TableCell>
                  <TableCell className="text-right">
                    {formatMoney(item.total_cotizador)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
